import * as yup from "yup";

const hazardousUpdateRequestDtoSchema = yup.object().shape({
  id: yup.number().required(),
  unNumber: yup
    .string()
    .required("UN Number is required")
    .matches(/^\d{1,7}$/, "UN Number must be a numeric value with maximum 7 digits"),
  adrClass: yup
    .string()
    .matches(/^\d+(\.\d+)?$/, {
      message: "Please enter a valid number (integer or decimal)",
      excludeEmptyString: true,
    })
    .required("ADR Class is a required field"),
  adrPackingGroup: yup
    .string()
    .typeError("ADR Packing Group must be a string")
    .matches(
      /^(?=[MDCLXVI])M*(C[MD]|D?C{0,3})(X[CL]|L?X{0,3})(I[XV]|V?I{0,3})$/,
      "ADR Packing Group must be a valid Roman numeral"
    )
    .required("ADR Packing Group is a required field"),
  adrNumberOfPackages: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .integer("ADR Number Of Packages must be an integer")
    .min(0, "ADR Number Of Packages must be at least zero")
    .max(9999999999, "ADR Number Of Packages must be 9999999999 or less")
    .required("ADR Number Of Packages is a required field"),
  adrNetWeight: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .integer("ADR Net Weight must be an integer")
    .min(0, "ADR Net Weight must be at least zero")
    .max(99999, "ADR Net Weight must be 99999 or less")
    .required("ADR Net Weight is a required field"),
  adrGrossWeight: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .integer("ADR Gross Weight must be an integer")
    .min(0, "ADR Gross Weight must be at least zero")
    .max(99999, "ADR Gross Weight must be 99999 or less")
    .required("ADR Gross Weight is a required field"),
});

const cargoUpdateRequestDtoSchema = yup.object().shape({
  id: yup.number().required(),
  numberOfPackages: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .typeError("Number of Packages must be a number")
    .integer("Number of Packages must be an integer")
    .positive("Number of Packages must be more than 0")
    .max(
      9999999999,
      "Number of Packages must be less than or equal to 9999999999"
    )
    .required("Number of Packages is a required field"),
  packageType: yup
    .string()
    .min(2, "Package Type must be at least 2 characters")
    .max(5, "Package Type must be at most 5 characters")
    .required("Package Type is required"),
  netWeight: yup
    .number()
    .typeError("Net Weight must be a number")
    .positive("Net Weight must be more than 0")
    .max(99999, "Net Weight must be 99999 or less")
    .required("Net Weight is required"),
  customsDocumentType: yup
    .string()
    .min(1, "Customs Document Type must be at least 1 character")
    .max(4, "Customs Document Type must be at most 4 characters")
    .required("Customs Document Type is required"),
});

export const validationSchema = yup.object().shape({
  status: yup.string().defined(),
  customsDocumentType: yup
    .string()
    .min(1, "Customs Document Type must be at least 1 character")
    .max(4, "Customs Document Type must be at most 4 characters")
    .required("Customs Document Type is required"),

  customerCode: yup
    .number()
    .typeError("Customer Code must be a number")
    .positive()
    .integer()
    .max(99999999, "Customer Code must be at most 8 digits")
    .required("Customer Code is required"),
  tareWeight: yup
    .number()
    .typeError("Tare Weight must be a number")
    .positive("Tare Weight must be a positive number")
    .integer()
    .max(99999, "Tare Weight must be at most 5 digits")
    .required("Tare Weight is required"),
  netWeight: yup
    .number()
    .typeError("Net Weight must be a number")
    .min(0, "Net Weight must be 0 or more")
    .max(99999, "Net Weight must be 99999 or less")
    .required("Net Weight is required")
    .test(
      "customs-doc-type-none-zero",
      "If Customs Document Type is 'NONE', Net Weight must be zero",
      function (value) {
        const { customsDocumentType } = this.parent;
        return customsDocumentType !== "NONE" || value === 0;
      }
    )
    .test(
      "customs-doc-type-none",
      "Net Weight cannot be zero unless Customs Document Type is 'NONE'",
      function (value) {
        const { customsDocumentType } = this.parent;
        return !(value === 0 && customsDocumentType !== "NONE");
      }
    ),

  unitType: yup.string().required("Unit Type is required"),
  plateNumber: yup
    .string()
    .required("Plate Number is required")
    .matches(
      /^[A-Z0-9-]+$/i,
      "Plate Number must contain only alphanumeric characters and hyphens"
    ),
  isCranable: yup.boolean().required(),
  iluCode: yup
    .string()
    .nullable()
    .defined()
    .when("isCranable", {
      is: true,
      then: (schema) =>
        schema
          .required("ILU Code is required")
          .matches(
            /^[A-Za-z]{4}\d{7}$/,
            "ILU Code must start with 4 letters followed by 7 numbers"
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  hazardousUpdateRequestDtos: yup
    .array()
    .of(hazardousUpdateRequestDtoSchema)
    .required(),
  cargoUpdateRequestDtos: yup
    .array()
    .of(cargoUpdateRequestDtoSchema)
    .required(),
});
